/**
 * Performance Enhancement Utilities
 * Addresses browser performance warnings and optimizes event handling
 */

/**
 * Enhance event listeners to use passive mode where appropriate
 * This helps prevent browser warnings about blocking events
 */
export function enhanceEventListeners() {
  // Override addEventListener to automatically apply passive mode for scroll-blocking events
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  
  // Events that should typically be passive for better performance
  const passiveEvents = ['touchstart', 'touchmove', 'wheel', 'mousewheel'];
  
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    // If options is a boolean (for capture), convert to object
    if (typeof options === 'boolean') {
      options = { capture: options };
    }
    
    // Default to object if no options provided
    if (!options) {
      options = {};
    }
    
    // Apply passive mode for scroll-blocking events unless explicitly disabled
    if (passiveEvents.includes(type) && options.passive !== false) {
      options.passive = true;
    }
    
    return originalAddEventListener.call(this, type, listener, options);
  };
}

/**
 * Optimize iframe interactions to prevent performance warnings
 */
export function optimizeIFramePerformance() {
  // Find all iframes and optimize their event handling
  const iframes = document.querySelectorAll('iframe');
  
  iframes.forEach(iframe => {
    // Ensure iframe content uses passive event listeners
    iframe.addEventListener('load', () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          // Apply passive event listeners to iframe content
          enhanceIFrameEvents(iframeDoc);
        }
      } catch (e) {
        // Cross-origin iframe - cannot access content
        console.log('Cross-origin iframe detected, skipping optimization');
      }
    }, { passive: true });
  });
}

/**
 * Enhance event listeners within iframe documents
 */
function enhanceIFrameEvents(doc) {
  const originalAddEventListener = doc.addEventListener;
  
  doc.addEventListener = function(type, listener, options) {
    if (typeof options === 'boolean') {
      options = { capture: options };
    }
    if (!options) {
      options = {};
    }
    
    // Make scroll-blocking events passive by default
    const passiveEvents = ['touchstart', 'touchmove', 'wheel', 'mousewheel'];
    if (passiveEvents.includes(type) && options.passive !== false) {
      options.passive = true;
    }
    
    return originalAddEventListener.call(this, type, listener, options);
  };
}

/**
 * Debounce function for performance optimization
 */
export function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * Throttle function for performance optimization  
 */
export function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Initialize all performance enhancements
 */
export function initializePerformanceEnhancements() {
  // Enhance event listeners globally
  enhanceEventListeners();
  
  // Optimize iframe performance
  optimizeIFramePerformance();
  
  // Re-run iframe optimization when new iframes are added
  const observer = new MutationObserver(debounce(() => {
    optimizeIFramePerformance();
  }, 250));
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  console.log('✅ Performance enhancements initialized');
}

/**
 * Fix common third-party library performance issues
 */
export function fixThirdPartyPerformanceIssues() {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyThirdPartyFixes);
  } else {
    applyThirdPartyFixes();
  }
}

function applyThirdPartyFixes() {
  // Fix common chat widget issues
  setTimeout(() => {
    // Look for common chat widget elements and optimize their events
    const chatElements = document.querySelectorAll('[class*="chat"], [id*="chat"], iframe[src*="chat"]');
    chatElements.forEach(element => {
      if (element.tagName === 'IFRAME') {
        element.addEventListener('load', () => {
          try {
            const iframeDoc = element.contentDocument || element.contentWindow?.document;
            if (iframeDoc) {
              enhanceIFrameEvents(iframeDoc);
            }
          } catch (e) {
            // Cross-origin, can't optimize
          }
        }, { passive: true });
      }
    });
  }, 1000);
} 